import * as amplitude from "@amplitude/analytics-browser";
import { get as getDeviceId } from './fingerprintManager.js';
import { getLocalISODateString } from './dateUtils.js';

// Source identifier to differentiate widget events from extension events
const EVENT_SOURCE = 'widget';

let amplitudeInitialized = false;
let amplitudeConfig = {
  apiKey: null,
  userId: null,
  widgetType: 'unknown',
  widgetVersion: typeof __WIDGET_VERSION__ !== 'undefined' ? __WIDGET_VERSION__ : 'unknown'
};

// Helper to check if we're in a test environment
const isTestEnvironment = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

/**
 * Get device info using User-Agent Client Hints (UA-CH) API first,
 * falling back to User-Agent string parsing for older browsers.
 * Returns structured device information for accurate analytics tracking.
 */
const getDeviceInfo = async () => {
  // Try User-Agent Client Hints API first (more accurate, available in Chrome/Edge)
  if (navigator.userAgentData) {
    try {
      const hints = await navigator.userAgentData.getHighEntropyValues([
        'platform',
        'platformVersion',
        'architecture',
        'model',
        'uaFullVersion',
        'fullVersionList'
      ]);

      const platform = hints.platform || 'unknown';
      const platformVersion = hints.platformVersion || '';
      // Device model from Client Hints (e.g., "iPhone 14", "MacBookPro18,3", "Pixel 7")
      // Falls back to 'Desktop' or 'Mobile' if not available
      const deviceModel = hints.model || (navigator.userAgentData?.mobile ? 'Mobile' : 'Desktop');

      // Map platform to os_name format expected by Amplitude
      let osName = platform;
      if (platform === 'Windows') osName = 'Windows';
      else if (platform === 'macOS') osName = 'macOS';
      else if (platform === 'Linux') osName = 'Linux';
      else if (platform === 'Android') osName = 'Android';
      else if (platform === 'iOS') osName = 'iOS';

      // Get browser info from Client Hints fullVersionList (more accurate than navigator.userAgentData.brands)
      let browserName = 'unknown';
      let browserVersion = hints.uaFullVersion || '';
      const brands = hints.fullVersionList || navigator.userAgentData?.brands || [];
      if (brands.length > 0) {
        // Filter out the gibberish brands (Not-A.Brand and any with semicolons)
        const validBrands = brands.filter(b =>
          !b.brand.includes(';') && b.brand !== 'Not-A.Brand'
        );

        // Prioritize specific browser names over generic "Chromium"
        // Order matters: check for Chrome, Edge, Opera first, then fall back to Chromium
        const brandPriority = ['Chrome', 'Edge', 'Opera', 'Brave', 'Chromium'];
        for (const priority of brandPriority) {
          const found = validBrands.find(b => b.brand.includes(priority));
          if (found) {
            browserName = found.brand;
            if (!browserVersion) browserVersion = found.version;
            break;
          }
        }
      }

      return {
        osName,
        osVersion: platformVersion,
        deviceModel,
        browserName,
        browserVersion,
        language: navigator.language || 'unknown',
        clientHintsSupported: true
      };
    } catch (e) {
      // Fall back to User-Agent parsing if Client Hints fails
      if (isTestEnvironment()) {
        console.warn('Client Hints failed, falling back to UA string:', e);
      }
    }
  }

  // Fallback: Parse from User-Agent string (for Firefox, Safari, older browsers)
  const userAgent = navigator.userAgent || '';

  let osName = 'unknown';
  let osVersion = '';
  if (userAgent.indexOf('Win') !== -1) {
    osName = 'Windows';
    const match = userAgent.match(/Windows NT ([0-9.]+)/);
    osVersion = match ? match[1] : '';
  } else if (userAgent.indexOf('Mac') !== -1) {
    osName = 'macOS';
    const match = userAgent.match(/Mac OS X ([0-9_.]+)/);
    osVersion = match ? match[1].replace(/_/g, '.') : '';
  } else if (userAgent.indexOf('Linux') !== -1) {
    osName = 'Linux';
  } else if (userAgent.indexOf('Android') !== -1) {
    osName = 'Android';
    const match = userAgent.match(/Android ([0-9.]+)/);
    osVersion = match ? match[1] : '';
  } else if (userAgent.indexOf('like Mac') !== -1 || userAgent.indexOf('iPhone') !== -1 || userAgent.indexOf('iPad') !== -1) {
    osName = 'iOS';
    const match = userAgent.match(/OS ([0-9_]+)/);
    osVersion = match ? match[1].replace(/_/g, '.') : '';
  }

  // Extract browser name and version from User-Agent
  let browserName = 'unknown';
  let browserVersion = '';
  if (userAgent.indexOf('Chrome') !== -1 && userAgent.indexOf('Edg') === -1) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/([0-9.]+)/);
    browserVersion = match ? match[1] : '';
  } else if (userAgent.indexOf('Firefox') !== -1) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/([0-9.]+)/);
    browserVersion = match ? match[1] : '';
  } else if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1) {
    browserName = 'Safari';
    const match = userAgent.match(/Version\/([0-9.]+)/);
    browserVersion = match ? match[1] : '';
  } else if (userAgent.indexOf('Edg') !== -1) {
    browserName = 'Edge';
    const match = userAgent.match(/Edg\/([0-9.]+)/);
    browserVersion = match ? match[1] : '';
  }

  // Device model cannot be reliably detected from User-Agent string
  // in modern browsers due to privacy restrictions (frozen/reduced UA strings)
  const isMobile = /Mobile|Android|iPhone|iPad|iPod/.test(userAgent);
  const deviceModel = isMobile ? 'Mobile' : 'Desktop';

  return {
    osName,
    osVersion,
    deviceModel,
    browserName,
    browserVersion,
    language: navigator.language || 'unknown',
    clientHintsSupported: false
  };
};

/**
 * Generate a session ID for tracking
 */
const generateSessionId = () => {
  if (typeof window === 'undefined') return `session_${Date.now()}`;
  
  let sessionId;
  try {
    sessionId = window.sessionStorage.getItem('gist_widget_session_id');
  } catch (e) {
    // sessionStorage blocked
  }
  
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    try {
      window.sessionStorage.setItem('gist_widget_session_id', sessionId);
    } catch (e) {
      // sessionStorage blocked, use in-memory only
    }
  }
  return sessionId;
};

/**
 * Get IP address (used for parity with extension tracking)
 * Exported for pre-fetching by widgets that need synchronous page close tracking
 */
export const getIPAddress = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip || 'unknown';
  } catch (error) {
    return 'unknown';
  }
};

/**
 * Map widget event names to Amplitude event names
 * These match the extension's event names for consistency
 */
export const AMPLITUDE_EVENT_MAP = {
  // Widget-specific events mapped to questions bar event names
  'Questions Bar Widget Initialized': 'questions_bar_widget_initialized',
  'Questions Bar Widget Visible': 'questions_bar_widget_visible',
  'Questions Bar Widget Question Clicked': 'questions_bar_widget_question_click',
  'Questions Bar Widget Question Visible': 'questions_bar_widget_question_visible',
  'Questions Bar Widget Questions Refreshed': 'questions_bar_widget_questions_refresh',
  'Questions Bar Widget Settings Changed': 'questions_bar_widget_settings_change',
  'Questions Bar Widget Page Close': 'questions_bar_widget_page_close',
  'Questions Bar Widget API Call': 'questions_bar_widget_api_call',
  'Questions Bar Widget API Error': 'questions_bar_widget_api_error',
  'Questions Bar Widget Question Tooltip Hovered': 'questions_bar_widget_question_tooltip_hovered',
};

/**
 * Initialize Amplitude SDK
 * @param {string} apiKey - Amplitude API key
 * @param {Object} config - Configuration options
 * @param {string} config.userId - User ID for tracking
 * @param {string} config.widgetType - Type of widget ('questions', 'chat', etc.)
 */
export async function initAmplitude(apiKey, config = {}) {
  if (amplitudeInitialized) {
    return;
  }

  // Use provided apiKey, or fall back to build-time injected value
  const finalApiKey = apiKey || (typeof __AMPLITUDE_API_KEY__ !== 'undefined' ? __AMPLITUDE_API_KEY__ : '');
  
  if (!finalApiKey) {
    return;
  }

  try {
    amplitudeConfig = {
      apiKey: finalApiKey,
      userId: config.userId || null,
      widgetType: config.widgetType || 'unknown',
      widgetVersion: typeof __WIDGET_VERSION__ !== 'undefined' ? __WIDGET_VERSION__ : 'unknown'
    };

    // Initialize Amplitude SDK
    await amplitude.init(finalApiKey, {
      defaultTracking: false,
      logLevel: amplitude.Types.LogLevel.None
    });

    // Set user properties via identify
    const identify = new amplitude.Identify();
    identify.set('user_agent', navigator.userAgent || '');
    identify.set('widget_version', amplitudeConfig.widgetVersion);
    identify.set('widget_type', amplitudeConfig.widgetType);
    identify.set('event_source', EVENT_SOURCE);

    // Get and set device info for accurate device identification
    try {
      const deviceInfo = await getDeviceInfo();
      identify.set('os_name', deviceInfo.osName);
      identify.set('os_version', deviceInfo.osVersion);
      identify.set('device_model', deviceInfo.deviceModel);
      identify.set('browser_name', deviceInfo.browserName);
      identify.set('browser_version', deviceInfo.browserVersion);
      identify.set('language', deviceInfo.language);
      identify.set('client_hints_supported', deviceInfo.clientHintsSupported);
    } catch (err) {
      if (isTestEnvironment()) {
        console.warn('Failed to set device info properties:', err);
      }
    }

    if (amplitudeConfig.userId) {
      identify.set('user_id', amplitudeConfig.userId);
    }

    amplitude.identify(identify);

    amplitudeInitialized = true;
  } catch (err) {
    console.error('Amplitude initialization error:', err);
  }
}

/**
 * Get base event data that all events include
 * Exported for pre-fetching by widgets that need synchronous page close tracking
 */
export const getBaseEventData = async () => {
  const deviceId = await getDeviceId();
  const deviceInfo = await getDeviceInfo();

  return {
    // Core tracking fields
    publisher_name: amplitudeConfig.widgetType === 'questions' ? 'PrtQuestionsWidget' : 'GistWidgets',
    page_url: window.location.href || '',
    domain: window.location.hostname || '',
    referrer_url: document.referrer || '',
    user_agent: navigator.userAgent || '',
    timestamp: Date.now(),
    session_id: generateSessionId(),

    // Widget-specific fields for differentiation
    source: EVENT_SOURCE,
    widget_type: amplitudeConfig.widgetType,
    widget_version: amplitudeConfig.widgetVersion,

    // Device identification
    device_id: deviceId || 'unknown',
    visitor_id: deviceId || 'unknown',

    // Device info (UA-CH primary, User-Agent fallback)
    os_name: deviceInfo.osName,
    os_version: deviceInfo.osVersion,
    device_model: deviceInfo.deviceModel,
    browser_name: deviceInfo.browserName,
    browser_version: deviceInfo.browserVersion,
    language: deviceInfo.language,
    client_hints_supported: deviceInfo.clientHintsSupported,

    // User identification (if available)
    ...(amplitudeConfig.userId && { user_id: amplitudeConfig.userId })
  };
};

/**
 * Track an event via Amplitude
 * @param {string} eventName - The event name (widget format)
 * @param {Object} eventProperties - Additional event properties
 */
export async function trackAmplitudeEvent(eventName, eventProperties = {}) {
  if (!amplitudeInitialized) {
    if (isTestEnvironment()) {
      console.warn('Amplitude not initialized, skipping event:', eventName);
    }
    return;
  }

  try {
    const baseData = await getBaseEventData();
    
    // Get IP address (async)
    const ipAddress = await getIPAddress();
    
    // Map to Amplitude event name
    const amplitudeEventName = AMPLITUDE_EVENT_MAP[eventName] || eventName;
    
    // Build the final payload
    const payload = {
      ...baseData,
      ip_address: ipAddress,
      // Include original event name for reference
      original_event_name: eventName,
      // Merge in any additional properties
      ...eventProperties,
      // Ensure source is always set correctly (override if provided in eventProperties)
      source: EVENT_SOURCE
    };

    // Send to Amplitude
    amplitude.track(amplitudeEventName, payload);
  } catch (err) {
    console.error('Amplitude tracking error:', err);
  }
}

/**
 * Check if Amplitude is initialized
 */
export function isAmplitudeInitialized() {
  return amplitudeInitialized;
}

/**
 * Get current Amplitude configuration (for debugging)
 */
export function getAmplitudeConfig() {
  return { ...amplitudeConfig, source: EVENT_SOURCE };
}

/**
 * Set user ID after initialization (useful for delayed auth)
 */
export function setAmplitudeUserId(userId) {
  if (!amplitudeInitialized) {
    console.warn('Cannot set user ID - Amplitude not initialized');
    return;
  }

  try {
    amplitude.setUserId(userId);
    amplitudeConfig.userId = userId;

    // Update identify
    const identify = new amplitude.Identify();
    identify.set('user_id', userId);
    amplitude.identify(identify);
  } catch (err) {
    console.error('Error setting Amplitude user ID:', err);
  }
}

/**
 * Set transport type for Amplitude (useful for page close tracking)
 * @param {string} transport - Transport type: 'fetch', 'xhr', or 'beacon'
 */
export function setAmplitudeTransport(transport) {
  if (!amplitudeInitialized) {
    console.warn('Cannot set transport - Amplitude not initialized');
    return;
  }

  try {
    amplitude.setTransport(transport);
  } catch (err) {
    console.error('Error setting Amplitude transport:', err);
  }
}

/**
 * Flush pending Amplitude events immediately
 * Use this with setTransport('beacon') before page unload to ensure events are sent
 */
export function flushAmplitudeEvents() {
  if (!amplitudeInitialized) {
    console.warn('Cannot flush - Amplitude not initialized');
    return;
  }

  try {
    amplitude.flush();
  } catch (err) {
    console.error('Error flushing Amplitude events:', err);
  }
}

/**
 * Track a page close event synchronously using pre-fetched data.
 * This is designed for use in beforeunload handlers where async operations won't complete.
 * @param {Object} baseData - Pre-fetched base event data (from getBaseEventData)
 * @param {number} dwellTimeSeconds - Dwell time in seconds
 * @param {number} dwellTimeMs - Dwell time in milliseconds
 * @param {string} ipAddress - Pre-fetched IP address
 */
export function trackPageCloseEvent(baseData, dwellTimeSeconds, dwellTimeMs, ipAddress) {
  if (!amplitudeInitialized) {
    return;
  }

  try {
    const amplitudeEventName = AMPLITUDE_EVENT_MAP['Questions Bar Widget Page Close'] || 'questions_bar_widget_page_close';

    const payload = {
      ...baseData,
      ip_address: ipAddress || 'unknown',
      dwell_time_seconds: dwellTimeSeconds,
      dwell_time_ms: dwellTimeMs,
      source: EVENT_SOURCE
    };

    // Set transport to beacon for reliable delivery
    amplitude.setTransport('beacon');

    // Send to Amplitude immediately
    amplitude.track(amplitudeEventName, payload);

    // Flush to ensure the event is sent before page unload
    amplitude.flush();
  } catch (err) {
    console.error('Page close tracking error:', err);
  }
}
